import { Cart } from '../../redux/types';
import { formatPrice, t } from '../../redux/utils';
import { itemKey, itemTitleKey } from '../../utils/trans';
import {
  Article,
  ConnectedArticle,
  ConnectedMileage,
  NewCartArticle,
  StaticCartArticle,
} from './rentware-cart-article/interfaces';
import { PriceCalculation } from '../../types/types';
import { getMileageAmountPerPeriod } from '../../utils/mileage';

export const makeArticles = ({
  articlesInCartCorrectOrder, //
  waitingArticles,
  cart,
  changeAmountCallback,
  deleteArticleCallback,
  onEdit,
}: {
  articlesInCartCorrectOrder: string[];
  waitingArticles: string[];
  cart: Cart;
  changeAmountCallback;
  deleteArticleCallback;
  onEdit;
}): Article[] => {
  if (!cart) {
    return [];
  }

  return articlesInCartCorrectOrder.map(name => {
    const item = cart.items.find(cartItem => cartItem.name === name); // this line prevents from changing order of articles

    const includedArticles = [];
    let isVariation = false;
    // add included articles in case of variation or bundle (more than 1 product)
    if (item.products?.length > 0) {
      if (item.products[0].variation) {
        isVariation = true;
        item.products[0].variation.items.forEach(item => {
          includedArticles.push({
            title: item.name,
            amount: item.quantity,
          });
        });
      } else if (item.products.length > 1) {
        // bundle case
        item.products.forEach(item => {
          includedArticles.push({
            title: item.name,
            amount: item.quantity,
          });
        });
      }
    }


    let title = t(itemKey(item.itemId), {}, item.name)
    let text = includedArticles.length === 0 ? t(itemTitleKey(item.itemId)) : ''
    if (item.isMileageOption) {
      title = getMileageAmountPerPeriod(item.mileageOption.relativeAmount.amount, item.mileageOption.config.includedAmount, item.mileageOption.relativeAmount.calculationType, t, item.mileageOption.config.unit)
      text = ''
      if (item.mileageOption.relativeAmount.amount !== item.mileageOption.amount) {
        text = t('article.mileageOptionSelectOption', { amount: item.mileageOption.amount, unit: item.mileageOption.config.unit })
      }
    }

    return {
      id: item.id,
      itemId: item.itemId,
      isWaiting: waitingArticles.includes(item.id),
      key: item.itemId,
      articleTitle: title,
      articleText: text,
      itemAmount: item.qty,
      minAmount: item.minQty,
      maxAmount: item.qty + item.availableQty,
      availableQty: item.availableQty,
      itemPrice: item.mileageOption && item.individualDiscountedPrice.gross === 0 ? t('article.mileageOptionZeroPrice') : formatPrice(item.individualDiscountedPrice.gross),
      totalPrice: item.discountedPrice,
      price: item.individualDiscountedPrice,
      priceCalculationType: item.priceCalculationType,
      imgSrc: item.image,
      isDeposit: item.isDeposit,
      removable: item.removable,
      isVariation: isVariation,
      parentIds: item.parentIds,
      isMileageOption: item.isMileageOption,
      mileageOption: item.mileageOption,
      includedArticles: includedArticles,
      availableMileageOptions: item.availableMileageOptions,
      changeAmountCallback: (newAmount, oldAmount) => changeAmountCallback(item.id, item.itemId, newAmount, oldAmount),
      deleteArticleCallback: () => deleteArticleCallback(item.id, item.itemId),
      onEdit: () => onEdit(item),
    };
  });
};

export const makeNewArticles = (articles: Article[], onMileageChange: (mileageItemId: string) => void, onVariationChange: (id: string) => void): NewCartArticle[] => {
  const newArticles: NewCartArticle[] = [];
  articles.forEach(article => {
    if (!article.isMileageOption && !article.isDeposit && article.parentIds.length === 0) {
      // 1. Look for mileages
      const connectedMileage = makeConnectedMileage(
        articles.find(mileageArticle => {
          return mileageArticle.isMileageOption && mileageArticle.itemId === article.itemId;
        }),
      );

      // 2. Look for deposits
      const connectedDeposit = makeConnectedArticle(
        articles.find(depositArticle => {
          return depositArticle.isDeposit && depositArticle.parentIds.includes(article.id);
        }),
      );

      // 3. Look for connectedArticles
      const connectedArticles: ConnectedArticle[] = [];

      articles.forEach(connectedArticle => {
        if (!connectedArticle.isMileageOption && !connectedArticle.isDeposit && connectedArticle.parentIds.includes(article.id)) {
          connectedArticles.push(makeConnectedArticle(connectedArticle));
        }
      });

      newArticles.push({
        id: article.id,
        itemId: article.itemId,
        isWaiting: article.isWaiting,
        articleTitle: article.articleTitle,
        articleText: article.articleText,
        itemAmount: article.itemAmount,
        minAmount: article.minAmount,
        availableAmount: article.availableQty,
        totalPrice: article.totalPrice,
        price: article.price,
        priceCalculationType: article.priceCalculationType,
        image: article.imgSrc,
        isVariation: article.isVariation,
        changeAmountCallback: article.changeAmountCallback,
        deleteArticleCallback: article.deleteArticleCallback,
        includedArticles: article.includedArticles,
        connectedArticles: connectedArticles,
        connectedMileage: connectedMileage,
        onMileageChange: onMileageChange,
        onVariationChange: onVariationChange,
        connectedDeposit: connectedDeposit,
      });
    }
  });

  return newArticles;
};

export const makeStaticArticles = (cart: Cart): StaticCartArticle[] => {
  if (!cart) {
    return [];
  }

  const articles = cart.items.map(item => {
    const includedArticles = [];
    let isVariation = false;
    // add included articles in case of variation or bundle (more than 1 product)
    if (item.products?.length > 0) {
      if (item.products[0].variation) {
        isVariation = true;
        item.products[0].variation.items.forEach(item => {
          includedArticles.push({
            title: item.name,
            amount: item.quantity,
          });
        });
      } else if (item.products.length > 1) {
        // bundle case
        item.products.forEach(item => {
          includedArticles.push({
            title: item.name,
            amount: item.quantity,
          });
        });
      }
    }

    return {
      id: item.id,
      itemId: item.itemId,
      key: item.itemId,
      articleTitle: item.mileageOption ? t('article.mileageOptionSelectOption', { amount: item.mileageOption.amount, unit: item.mileageOption.config.unit }) : t(itemKey(item.itemId), {}, item.name),
      articleText: includedArticles.length === 0 ? t(itemTitleKey(item.itemId)) : '',
      itemAmount: item.qty,
      itemPrice: item.mileageOption && item.individualDiscountedPrice.gross === 0 ? t('article.mileageOptionZeroPrice') : formatPrice(item.individualDiscountedPrice.gross),
      totalPrice: item.discountedPrice,
      price: item.individualDiscountedPrice,
      priceCalculationType: item.priceCalculationType,
      imgSrc: item.image,
      isDeposit: item.isDeposit,
      isVariation: isVariation,
      parentIds: item.parentIds,
      isMileageOption: item.isMileageOption,
      mileageOption: item.mileageOption,
      includedArticles: includedArticles,
      availableMileageOptions: item.availableMileageOptions,
    };
  });

  const newArticles: StaticCartArticle[] = [];

  articles.forEach(article => {
    if (!article.isMileageOption && !article.isDeposit && article.parentIds.length === 0) {
      // 1. Look for mileages
      const connectedMileage = makeConnectedMileage(
        articles.find(mileageArticle => {
          return mileageArticle.isMileageOption && mileageArticle.itemId === article.itemId;
        }),
      );

      // 2. Look for deposits
      const connectedDeposit = makeConnectedArticle(
        articles.find(depositArticle => {
          return depositArticle.isDeposit && depositArticle.parentIds.includes(article.id);
        }),
      );

      // 3. Look for connectedArticles
      const connectedArticles: ConnectedArticle[] = [];

      articles.forEach(connectedArticle => {
        if (!connectedArticle.isMileageOption && !connectedArticle.isDeposit && connectedArticle.parentIds.includes(article.id)) {
          connectedArticles.push(makeConnectedArticle(connectedArticle));
        }
      });

      let price = article.price
      if (!price.priceCalculation) {
        price.priceCalculation = article.priceCalculationType
      }

      newArticles.push({
        id: article.id,
        itemId: article.itemId,
        articleTitle: article.articleTitle,
        articleText: article.articleText,
        itemAmount: article.itemAmount,
        totalPrice: {...article.totalPrice, priceCalculation: PriceCalculation.Interval},
        price: price,
        priceCalculationType: article.priceCalculationType,
        image: article.imgSrc,
        isVariation: article.isVariation,
        includedArticles: article.includedArticles,
        connectedArticles: connectedArticles,
        connectedMileage: connectedMileage,
        connectedDeposit: connectedDeposit,
      });
    }
  });

  return newArticles;
};

const makeConnectedMileage = (mileageArticle: Article): ConnectedMileage => {
  if (!mileageArticle) {
    return null;
  }

  let price = mileageArticle.price
  if (!price.priceCalculation) {
    price.priceCalculation = mileageArticle.priceCalculationType
  }

  const selectedOption = mileageArticle.mileageOption
  if (selectedOption && selectedOption.price && !selectedOption.price.priceCalculation) {
    selectedOption.price.priceCalculation = price.priceCalculation
  }

  const title = getMileageAmountPerPeriod(selectedOption.relativeAmount.amount, selectedOption.config.includedAmount, selectedOption.price.priceCalculation, t, selectedOption.config.unit)
  const text = mileageArticle.articleTitle
  return {
    mileageTitle: title,
    mileageText: text,
    totalPrice: {...mileageArticle.totalPrice, priceCalculation: PriceCalculation.Interval},
    price: price,
    selectedOption: selectedOption,
    availableOptions: mileageArticle.availableMileageOptions,
  };
};

const makeConnectedArticle = (article: Article): ConnectedArticle => {
  if (!article) {
    return null;
  }

  let price = article.price
  if (!price.priceCalculation) {
    price.priceCalculation = article.priceCalculationType
  }

  return {
    title: article.articleTitle,
    image: article.imgSrc,
    price: price,
  };
};
